import axios, { AxiosError, type AxiosRequestConfig } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
const CDN_URL = import.meta.env.VITE_CDN_URL || '';

// Helper function to get asset URL (CDN or API)
export const getAssetUrl = (path: string): string => {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  return CDN_URL ? `${CDN_URL}${path}` : path;
};

// Type definitions
export interface ApiError {
  message: string;
  status: number;
  errors?: Record<string, string[]>;
}

export interface User {
  id: string;
  email: string;
  role: 'trainer' | 'client';
  first_name: string;
  last_name: string;
  profile_photo_url?: string;
}

export interface LoginResponse {
  token: string;
  user: User;
}

export interface TrainerProfile {
  id: string;
  user_id: string;
  bio: string;
  certifications: string[];
  specialties: string[];
  years_experience: number;
  is_verified: boolean;
}

export interface ClientProfile {
  id: string;
  user_id: string;
  age: number;
  sex: 'male' | 'female' | 'other' | 'prefer_not_to_say';
  body_type: string;
  weight: number;
  weight_unit: 'kg' | 'lbs';
  height: number;
  height_unit: 'cm' | 'inches';
}

export interface Client {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  profile_photo_url?: string;
  subscription?: Subscription | null;
  assigned_plans: TrainingPlan[];
  profile?: ClientProfile;
}

export interface Subscription {
  id: string;
  user_id: string;
  tier: 'free' | 'standard' | 'premium';
  platform: 'apple' | 'google';
  expires_at: string;
  status: 'active' | 'expired' | 'cancelled' | 'grace_period';
}

export interface TrainingPlan {
  id: string;
  trainer_id: string;
  title: string;
  description: string;
  tier: 'free' | 'standard' | 'premium';
  duration_weeks?: number;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  is_published: boolean;
  thumbnail_url?: string;
}

export interface Occasion {
  id: string;
  plan_id: string;
  title: string;
  description?: string;
  order: number;
  estimated_duration_minutes: number;
}

export interface Video {
  id: string;
  trainer_id: string;
  title: string;
  description?: string;
  mux_asset_id: string;
  mux_playback_id: string;
  duration_seconds: number;
  category: 'strength' | 'cardio' | 'mobility' | 'flexibility' | 'other';
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  /** Signed thumbnail URL from API (required for signed Mux playback IDs) */
  thumbnail_url?: string;
  /** Signed animated GIF preview URL from API */
  animated_thumbnail_url?: string;
}

/** Backend returns paginated { data: Video[], meta?, links? }; supports plain array for compatibility */
export type VideoListResponse = { data: Video[] } | Video[];

export interface Analytics {
  active_clients: number;
  monthly_revenue: number;
  total_plans: number;
  total_videos: number;
}

// Create axios instance
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track if we're currently refreshing the token
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    // Handle 401 errors with token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('auth_token');
      if (!refreshToken) {
        // No token to refresh, redirect to login
        localStorage.removeItem('auth_token');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        // Attempt to refresh the token
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {}, {
          headers: {
            Authorization: `Bearer ${refreshToken}`,
          },
        });

        const { token } = response.data;
        localStorage.setItem('auth_token', token);
        
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
        }

        processQueue(null, token);
        isRefreshing = false;

        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as Error, null);
        isRefreshing = false;
        localStorage.removeItem('auth_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Handle other errors
    const apiError: ApiError = {
      message: 'An error occurred',
      status: error.response?.status || 500,
    };

    if (error.response?.data) {
      const errorData = error.response.data as { message?: string; errors?: Record<string, string[]> };
      apiError.message = errorData.message || apiError.message;
      apiError.errors = errorData.errors;
    }

    return Promise.reject(apiError);
  }
);

// API Service Methods

// Auth API
export const authApi = {
  register: (data: {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    role: 'trainer' | 'client';
  }) => apiClient.post<LoginResponse>('/auth/register', data),

  login: (email: string, password: string) =>
    apiClient.post<LoginResponse>('/auth/login', { email, password }),

  logout: () => apiClient.post('/auth/logout'),

  refresh: () => apiClient.post<{ token: string }>('/auth/refresh'),

  me: () => apiClient.get<User>('/auth/me'),
};

// Trainer Profile API
export const trainerProfileApi = {
  get: () => apiClient.get<TrainerProfile>('/trainer/profile'),

  create: (data: Partial<TrainerProfile>) =>
    apiClient.post<TrainerProfile>('/trainer/profile', data),

  update: (data: Partial<TrainerProfile>) =>
    apiClient.put<TrainerProfile>('/trainer/profile', data),
};

// Client Management API
export const clientApi = {
  list: () => apiClient.get<Client[]>('/trainer/clients'),

  get: (clientId: string) => apiClient.get<Client>(`/trainer/clients/${clientId}`),

  getProfile: (clientId: string) =>
    apiClient.get<ClientProfile>(`/trainer/clients/${clientId}/profile`),
};

// Client Profile API
export const clientProfileApi = {
  get: () => apiClient.get<ClientProfile>('/client/profile'),

  create: (data: Omit<ClientProfile, 'id' | 'user_id'>) =>
    apiClient.post<ClientProfile>('/client/profile', data),

  update: (data: Partial<Omit<ClientProfile, 'id' | 'user_id'>>) =>
    apiClient.put<ClientProfile>('/client/profile', data),
};

// Training Plan API
export const planApi = {
  list: () => apiClient.get<TrainingPlan[]>('/plans'),

  get: (id: string) => apiClient.get<TrainingPlan>(`/plans/${id}`),

  create: (data: Partial<TrainingPlan>) =>
    apiClient.post<TrainingPlan>('/plans', data),

  update: (id: string, data: Partial<TrainingPlan>) =>
    apiClient.put<TrainingPlan>(`/plans/${id}`, data),

  delete: (id: string) => apiClient.delete(`/plans/${id}`),

  publish: (id: string, tier: 'free' | 'standard' | 'premium') =>
    apiClient.post<TrainingPlan>(`/plans/${id}/publish`, { tier }),

  assign: (id: string, clientId: string) =>
    apiClient.post(`/plans/${id}/assign`, { client_id: clientId }),

  getAssigned: () => apiClient.get<TrainingPlan[]>('/plans/assigned'),

  getOccasions: (id: string) => apiClient.get<Occasion[]>(`/plans/${id}/occasions`),
};

// Occasion API
export const occasionApi = {
  create: (planId: string, data: Partial<Occasion>) =>
    apiClient.post<Occasion>(`/plans/${planId}/occasions`, data),

  update: (id: string, data: Partial<Occasion>) =>
    apiClient.put<Occasion>(`/plans/occasions/${id}`, data),

  delete: (id: string) => apiClient.delete(`/plans/occasions/${id}`),

  getVideos: (id: string) => apiClient.get<Video[]>(`/occasions/${id}/videos`),

  attachVideo: (occasionId: string, videoId: string, order: number) =>
    apiClient.post(`/occasions/${occasionId}/videos`, { video_id: videoId, order }),

  detachVideo: (occasionId: string, videoId: string) =>
    apiClient.delete(`/plans/occasions/${occasionId}/videos/${videoId}`),
};

// Video API
export const videoApi = {
  list: (params?: { category?: string; difficulty?: string; search?: string }) =>
    apiClient.get<VideoListResponse>('/videos', { params }),

  get: (id: string) => apiClient.get<Video>(`/videos/${id}`),

  create: (data: Partial<Video>) => apiClient.post<Video>('/videos', data),

  update: (id: string, data: Partial<Video>) =>
    apiClient.put<Video>(`/videos/${id}`, data),

  delete: (id: string) => apiClient.delete(`/videos/${id}`),

  getPlaybackUrl: (id: string) =>
    apiClient.get<{ playback_url: string; expires_in: number }>(`/videos/${id}/playback-url`),

  createUploadUrl: () =>
    apiClient.post<{ upload_url: string; upload_id: string }>('/videos/upload-url'),

  getUploadStatus: (uploadId: string) =>
    apiClient.get<{
      ready: boolean;
      asset_id?: string;
      playback_id?: string;
      duration_seconds?: number;
      message?: string;
    }>(`/videos/upload-status/${uploadId}`),
};

// Subscription API
export const subscriptionApi = {
  validate: (receipt: string, platform: 'apple' | 'google') =>
    apiClient.post<Subscription>('/subscriptions/validate', { receipt, platform }),

  getActive: () => apiClient.get<Subscription>('/subscriptions/active'),

  cancel: () => apiClient.post('/subscriptions/cancel'),
};

// Progress API
export const progressApi = {
  complete: (occasionId: string, data?: { duration_seconds?: number; notes?: string }) =>
    apiClient.post('/progress/complete', { occasion_id: occasionId, ...data }),

  getPlanProgress: (planId: string) =>
    apiClient.get<{
      completed_occasions: number;
      total_occasions: number;
      percentage: number;
      progress: Array<{ occasion_id: string; completed_at: string }>;
    }>(`/progress/plan/${planId}`),
};

// Analytics API
export const analyticsApi = {
  getDashboard: () => apiClient.get<Analytics>('/trainer/dashboard'),

  getRevenue: (year: number, month: number) =>
    apiClient.get<{ revenue: number }>('/trainer/analytics/revenue', {
      params: { year, month },
    }),

  getClients: () =>
    apiClient.get<Array<{ client_id: string; progress: number }>>('/trainer/analytics/clients'),
};

// Plan Assignment API
export const assignmentApi = {
  delete: (id: string) => apiClient.delete(`/assignments/${id}`),
};
